import React, { useState, useEffect } from 'react';

const CalendarSingleDate = ({ onDateSelect, selectedDate: externalSelectedDate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  
  useEffect(() => {
    setSelectedDate(externalSelectedDate || null);
  }, [externalSelectedDate]);
  
  const monthNames = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  const dayNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  const isFutureDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate > today;
  };

  const isSunday = (date) => {
    return date.getDay() === 0;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && 
           date.getMonth() === today.getMonth() && 
           date.getFullYear() === today.getFullYear();
  };

  const canGoToPrevMonth = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    
    return currentMonth > today;
  };

  const canGoToNextMonth = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    const maxMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1); 
    
    return nextMonth <= maxMonth;
  };

  const generateCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay(); 
    
    const calendar = [];
    
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = 0; i < startingDay; i++) {
      const dayDate = new Date(year, month - 1, prevMonthLastDay - startingDay + i + 1);
      calendar.push({
        day: prevMonthLastDay - startingDay + i + 1,
        month: 'prev',
        date: dayDate,
        disabled: true
      });
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateForCheck = new Date(date);
      dateForCheck.setHours(0, 0, 0, 0);
      
      calendar.push({
        day,
        month: 'current',
        date,
        disabled: !isFutureDate(date) || isSunday(date) || isToday(date)
      });
    }
    
    const totalCells = 42; 
    const remainingCells = totalCells - calendar.length;
    for (let i = 1; i <= remainingCells; i++) {
      calendar.push({
        day: i,
        month: 'next',
        date: new Date(year, month + 1, i),
        disabled: true
      });
    }
    
    return calendar;
  };

  const handleDayClick = (dayInfo) => {
    if (dayInfo.disabled || dayInfo.month !== 'current') return;
    
    const clickedDate = new Date(dayInfo.date);
    clickedDate.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (clickedDate <= today || isSunday(clickedDate)) {
      return;
    }
    
    setSelectedDate(clickedDate);
    
    if (onDateSelect) {
      onDateSelect({
        selectedDate: clickedDate,
        startDate: null,
        endDate: null,
        isSelectingRange: false
      });
    }
  };

  const isDateSelected = (date) => {
    if (!selectedDate) return false;
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    const selectedDateCopy = new Date(selectedDate);
    selectedDateCopy.setHours(0, 0, 0, 0);
    return dateToCheck.getTime() === selectedDateCopy.getTime();
  };

  const goToPrevMonth = () => {
    if (!canGoToPrevMonth()) return;
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    if (!canGoToNextMonth()) return;
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDate(null);
    
    if (onDateSelect) {
      onDateSelect({
        selectedDate: null,
        startDate: null,
        endDate: null,
        isSelectingRange: false
      });
    }
  };

  const calendarDays = generateCalendar();

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPrevMonth}
          disabled={!canGoToPrevMonth()}
          className={`p-2 transition-colors rounded-full ${canGoToPrevMonth() ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'}`}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="#184A7D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <h2 className="text-base tracking-widest text-center md:text-lg font-itcbold text-blue">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        
        <button
          onClick={goToNextMonth}
          disabled={!canGoToNextMonth()}
          className={`p-2 transition-colors rounded-full ${canGoToNextMonth() ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'}`}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.5 15L12.5 10L7.5 5" stroke="#184A7D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      
      <div className="grid grid-cols-7 mt-2 text-center">
        {dayNames.map((day, index) => (
          <div key={`day-${index}`}>
            <div
              className={`font-itcbold text-base md:text-lg
                  ${index === 0 ? 'text-green' : 'text-blue'} `}>
              {day}
            </div>
            <div className='w-full bg-blue h-[2px]'></div>
          </div>
        ))}

        {calendarDays.map((dayInfo, index) => {
          const isSelected = isDateSelected(dayInfo.date);
          const isDaySunday = isSunday(dayInfo.date);
          const isDayToday = isToday(dayInfo.date);
          const isPast = !isFutureDate(dayInfo.date) && !isDayToday;
          
          return (
            <button
              key={index}
              onClick={() => handleDayClick(dayInfo)}
              disabled={dayInfo.disabled}
              className={`
                p-2 rounded-full text-center transition-all duration-200
                font-itcbold text-base md:text-lg
                ${dayInfo.disabled ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-blue/10'}
                ${dayInfo.month !== 'current' ? 'text-gray-400' : ''}
                ${isSelected ? 'bg-[#00769680] text-black' : ''}
                ${index % 7 === 0 && !dayInfo.disabled ? 'text-green' : ''}
                ${!dayInfo.disabled && dayInfo.month === 'current' && index % 7 !== 0 ? 'text-black' : ''}
                ${isDayToday ? 'ring-2 ring-green' : ''}
                ${isDaySunday && dayInfo.month === 'current' ? 'text-red-500' : ''}
                ${isPast ? 'text-gray-300' : ''}
              `}
            >
              {dayInfo.day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarSingleDate;